/**
 * Indicator Panel
 *
 * Dropdown button in the chart toolbar that lets users:
 * - Browse available indicators by category
 * - Add indicators to the chart
 * - See active indicators with toggle visibility / remove
 * - Edit indicator parameters inline
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { indicatorEngine } from "../../indicators/engine/indicator-engine";
import { ActiveIndicator, IndicatorCategory } from "../../indicators/types/indicator.types";
import { IndicatorListItem } from "../../store/metadata-slice";
import { normalizeIndicatorKey, getIndicatorLicenseStatus, getIndicatorExpiryInfo, formatExpiryForDisplay } from "../../indicators/engine/indicator-license";
import { useIndicatorLicenseContext } from "../../hooks/useIndicatorLicenseContext";
import "./IndicatorPanel.scss";

interface IndicatorPanelProps {
  activeIndicators: ActiveIndicator[];
  onAdd: (indicatorId: string) => void;
  onRemove: (instanceId: string) => void;
  onRemoveAll: () => void;
  onToggleVisibility: (instanceId: string) => void;
  onParamsChange: (instanceId: string, params: Record<string, any>) => void;
  colorTheme: string;
  compact?: boolean;
}

// Categories merged into the single "Strategies" tab.
const MERGED_GROUP_CATEGORIES: IndicatorCategory[] = [
  "trend",
  "momentum",
  "volatility",
  "volume",
  "support_resistance",
  "advanced",
  "sessions",
];

type IndicatorTabKey = "group" | "strategies";

const CATEGORIES: { key: IndicatorTabKey; label: string; categories: IndicatorCategory[] }[] = [
  { key: "group", label: "Strategies", categories: MERGED_GROUP_CATEGORIES },
  { key: "strategies", label: "Premium Strategies", categories: ["strategies"] },
];


const IndicatorPanel: React.FC<IndicatorPanelProps> = ({
  activeIndicators,
  onAdd,
  onRemove,
  onRemoveAll,
  onToggleVisibility,
  onParamsChange,
  colorTheme,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<IndicatorTabKey>("group");
  const [search, setSearch] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Close on outside click — covers both the button wrapper and the portal dropdown
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inPanel = panelRef.current && panelRef.current.contains(target);
      const inPortal = document.getElementById("indicator-dropdown-portal")?.contains(target);
      if (!inPanel && !inPortal) setIsOpen(false);
    };
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  // Recalculate dropdown position whenever it opens or on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    const update = () => {
      const rect = btnRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dropdownWidth = 320;
      const viewportWidth = window.innerWidth;
      // Prefer left-aligned; if it would overflow right, right-align instead
      let left = rect.left;
      if (left + dropdownWidth > viewportWidth - 8) left = viewportWidth - dropdownWidth - 8;
      if (left < 8) left = 8;
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left,
        width: dropdownWidth,
        zIndex: 99999,
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isOpen]);

  const allowedIndicators: string[] = useSelector(
    (state: any) => state.metadata?.indicatorState?.allowedIndicators ?? []
  );
  const metadataIndicatorList: Array<string | IndicatorListItem> = useSelector(
    (state: any) => state.metadata?.indicatorState?.indicatorList ?? []
  );
  const licenseCtx = useIndicatorLicenseContext();

  const normalizedMetadataStrategySet = useMemo(
    () =>
      new Set(
        metadataIndicatorList
          .flatMap((item) => {
            if (typeof item === "string") {
              return [normalizeIndicatorKey(item)];
            }
            return [normalizeIndicatorKey(item?.id), normalizeIndicatorKey(item?.name)];
          })
          .filter(Boolean)
      ),
    [metadataIndicatorList]
  );

  const isExpired = useCallback(
    (id: string): boolean => {
      const def = indicatorEngine.get(id);
      if (!def) return false;
      return getIndicatorLicenseStatus(def, licenseCtx).expired;
    },
    [licenseCtx]
  );

  const hasStrategyLicense = useCallback(
    (indicator: { id: string; name: string; shortName: string; category: IndicatorCategory }) =>
      !getIndicatorLicenseStatus(indicator, licenseCtx).unlicensed,
    [licenseCtx]
  );

  // Apply master-list filter for non-strategy categories.
  const masterListFiltered = useMemo(() => {
    const all = indicatorEngine.getAll();
    return allowedIndicators.length === 0
      ? all
      : all.filter((ind) => ind.category === "strategies" || allowedIndicators.includes(ind.id));
  }, [allowedIndicators]);

  // Strategies are shown strictly from /metadata indicatorList and are
  // enabled/disabled by license checks in hasStrategyLicense.
  // If metadata has no strategies, show none in Premium Strategies tab.
  const allIndicators = useMemo(() => {
    const strategyIndicators = masterListFiltered.filter((ind) => ind.category === "strategies");
    const nonStrategyIndicators = masterListFiltered.filter((ind) => ind.category !== "strategies");

    const metadataStrategies = strategyIndicators.filter((ind) => {
      // Match only on display name / shortName, not on the local internal ID.
      const nameKey = normalizeIndicatorKey(ind.name);
      const shortNameKey = normalizeIndicatorKey(ind.shortName);
      return (
        normalizedMetadataStrategySet.has(nameKey) ||
        normalizedMetadataStrategySet.has(shortNameKey)
      );
    });

    return [...nonStrategyIndicators, ...metadataStrategies];
  }, [masterListFiltered, normalizedMetadataStrategySet]);

  const selectedTabCategories = CATEGORIES.find(tab => tab.key === selectedTab)?.categories ?? [];
  const categoryIndicators = allIndicators.filter(ind => selectedTabCategories.includes(ind.category));
  const filteredIndicators = (search.trim()
    ? allIndicators.filter(
        ind =>
          ind.name.toLowerCase().includes(search.toLowerCase()) ||
          ind.shortName.toLowerCase().includes(search.toLowerCase())
      )
    : categoryIndicators
  ).slice().sort((a, b) => a.name.localeCompare(b.name));

  const handleAdd = (indicatorId: string) => {
    onAdd(indicatorId);
    // Stay open so user can add more
  };

  return (
    <div className={`indicator-panel-wrapper ${colorTheme}`} ref={panelRef}>
      <button
        ref={btnRef}
        className="indicator-btn"
        onClick={() => setIsOpen(!isOpen)}
        title="Indicators"
      >
        <span className="indicator-icon">ƒ</span>
        <span className="indicator-label">Indicator</span>
        {activeIndicators.length > 0 && (
          <span className="indicator-count">{activeIndicators.length}</span>
        )}
        <span className="dropdown-arrow">▼</span>
      </button>

      {isOpen && createPortal(
        // eslint-disable-next-line react/forbid-dom-props
        <div id="indicator-dropdown-portal" className={`indicator-dropdown indicator-dropdown-portal ${colorTheme}`} style={dropdownStyle}>
          {/* Header: search + close */}
          <div className="indicator-dropdown-header">
            <div className="indicator-search">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search indicators..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape" && search) {
                    e.stopPropagation();
                    setSearch("");
                  }
                }}
                autoFocus
              />
              {search.length > 0 && (
                <button
                  type="button"
                  className="indicator-search-clear"
                  onClick={() => {
                    setSearch("");
                    searchInputRef.current?.focus();
                  }}
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <button
              className="indicator-close-btn"
              onClick={() => setIsOpen(false)}
              title="Close"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          {/* Browse */}
          <div className="indicator-browse">
              {!search.trim() && (
                <div className="category-tabs">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.key}
                      className={`cat-btn ${selectedTab === cat.key ? "active" : ""}`}
                      onClick={() => setSelectedTab(cat.key)}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="indicator-list">
                {filteredIndicators.map(ind => {
                  const instanceCount = activeIndicators.filter(a => a.indicatorId === ind.id).length;
                  const expired = isExpired(ind.id);
                  const unlicensed = !hasStrategyLicense(ind as any);
                  const disabled = expired || unlicensed;
                  const disabledReason = expired
                    ? "License expired for this indicator"
                    : unlicensed
                      ? "License not available for this indicator"
                      : ind.name;
                  const expiryInfo = getIndicatorExpiryInfo(ind as any, licenseCtx);
                  return (
                    <div
                      key={ind.id}
                      className={`indicator-item${disabled ? " indicator-item--disabled" : ""}`}
                      onClick={() => !disabled && handleAdd(ind.id)}
                      title={disabledReason}
                    >
                      <div className="indicator-item-info">
                        <div className="indicator-item-text">
                          <span className="indicator-item-name">{ind.name}</span>
                          {expiryInfo && (
                            <span className={`indicator-item-expiry${expiryInfo.expired ? " indicator-item-expiry--expired" : " indicator-item-expiry--valid"}`}>
                              {`(valid: ${formatExpiryForDisplay(expiryInfo.expiryDate)})`}
                            </span>
                          )}
                        </div>
                        <span className="indicator-item-badge">{ind.pane === "overlay" ? "Overlay" : "Sub"}</span>
                        {!disabled && instanceCount > 0 && (
                          <span className="indicator-instance-count" title={`${instanceCount} active instance${instanceCount > 1 ? 's' : ''}`}>
                            {instanceCount}
                          </span>
                        )}
                      </div>
                      {disabled ? (
                        <span className="indicator-item-lock" title={expired ? "License expired" : "License not available"}>
                          <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                          </svg>
                        </span>
                      ) : (
                        <span className="indicator-item-add">+</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default IndicatorPanel;
